/**
 * Dynamic grid `filterColumnsType` — selectable column types:
 *
 * Configure via TabsConfiguration API (`filterColumnsType` on each tab header).
 * Options come from `jsonOptionData` on the same header (array of `{ value, label, ... }`).
 *
 * | Type | Stored in DB / API row | Read-only grid display | When to use |
 * |------|------------------------|------------------------|-------------|
 * | `dropdown` | `option.value` (ID/code) | Label resolved from `jsonOptionData` | Backend stores stable IDs; UI shows human-readable labels. |
 * | `multiselect` | Comma-separated `option.value`s | Labels resolved from `jsonOptionData` | Same as `dropdown`, but multiple values. |
 * | `dropdownvaluebylabel` | `option.label` (display text) | Raw API value as-is (no ID lookup) | Backend already stores the display text, or labels are the source of truth. |
 * | `multiselectvaluebylabel` | Comma-separated `option.label`s | Raw API value as-is (no ID lookup) | Same as `dropdownvaluebylabel`, but multiple values. |
 *
 * **Choosing a type**
 * - Use `dropdown` / `multiselect` when the grid API returns IDs and TabConfiguration maps them to labels.
 * - Use `dropdownvaluebylabel` / `multiselectvaluebylabel` when the grid API already returns the text
 *   users should see (e.g. `"Active"`, `"Pending, Approved"`). No value→label translation runs on display.
 *
 * Edit mode and column filters still render a react-select from `jsonOptionData` for all four types.
 * On save, `dropdown`/`multiselect` send values; `*valuebylabel` types send labels.
 * Column search filters always send `option.value` in the payload for all selectable types.
 *
 * ---
 *
 * Dynamic grid `conditionalFields` — **FilterDependentData** contract:
 *
 * - **`Column`**: Control column (e.g. `InstrumentName`). When the user picks a value here,
 *   that choice drives which options appear in dependent dropdowns.
 * - **`DependentFilteredFields`**: Each entry targets one dependent column:
 *   - **`Field`**: `columnKey` of the column whose **`jsonOptionData`** list should be filtered.
 *   - **`FilterOn`**: Property name present on **both** the control option objects and each
 *     dependent option object. Dependent options are kept when
 *     `dependentOption[FilterOn] === controlFilterValue`, where `controlFilterValue` is taken from
 *     the **selected control option**’s `FilterOn` (preferred), or from `row[FilterOn]` if set
 *     on the row (e.g. hidden id column).
 *
 * Example: control `InstrumentName` options like `{ value, label, InstrumentId }`; dependent
 * `TestName` options like `{ value, label, InstrumentId }` — filter with `FilterOn: "InstrumentId"`.
 */

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

/** Dynamic grid `filterColumnsType` values — use in TabsConfiguration API. */
export const FilterTypeColumns = Object.freeze({
  TEXT: "text",
  NUMBER: "number",
  DATEPICKER: "datepicker",
  /** Stores option.value; grid display resolves label via jsonOptionData. */
  DROPDOWN: "dropdown",
  /** Stores option.label; grid display shows API value as-is. */
  DROPDOWN_VALUE_BY_LABEL: "dropdownvaluebylabel",
  /** Stores comma-separated option.values; grid display resolves labels. */
  MULTI_SELECT: "multiselect",
  /** Stores comma-separated option.labels; grid display shows API value as-is. */
  MULTI_SELECT_VALUE_BY_LABEL: "multiselectvaluebylabel",
  DATE_RANGE: "daterange",
  TEXT_DATE: "textdate",
});

export function isFilterDependentDataAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("FilterDependentData");
}

export function isShowHideAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("ShowHide");
}

export function isShowSelectedValueAction(cfg: any): boolean {
  return norm(cfg?.ActionType) === norm("ShowSelectedValue");
}

export function parseJsonOptionsArray(raw: string | null | undefined): any[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Match option.value to row value (string/number tolerant). */
export function optionValueMatchesRow(
  optionValue: any,
  rowValue: any
): boolean {
  if (rowValue === undefined || rowValue === null || rowValue === "") {
    return false;
  }
  return String(optionValue ?? "") === String(rowValue ?? "");
}

export function isDropdownFilterType(inputType: any): boolean {
  const t = norm(inputType);
  return (
    t === FilterTypeColumns.DROPDOWN ||
    t === FilterTypeColumns.DROPDOWN_VALUE_BY_LABEL
  );
}

export function isMultiSelectFilterType(inputType: any): boolean {
  const t = norm(inputType);
  return (
    t === FilterTypeColumns.MULTI_SELECT ||
    t === FilterTypeColumns.MULTI_SELECT_VALUE_BY_LABEL
  );
}

export function isSelectableFilterType(inputType: any): boolean {
  return isDropdownFilterType(inputType) || isMultiSelectFilterType(inputType);
}

export function storesLabelInRow(inputType: any): boolean {
  const t = norm(inputType);
  return (
    t === FilterTypeColumns.DROPDOWN_VALUE_BY_LABEL ||
    t === FilterTypeColumns.MULTI_SELECT_VALUE_BY_LABEL
  );
}

/** Match option to row value using value or label depending on filter column type. */
export function optionMatchesStoredRowValue(
  option: any,
  rowValue: any,
  inputType: any
): boolean {
  if (rowValue === undefined || rowValue === null || rowValue === "") {
    return false;
  }
  if (storesLabelInRow(inputType)) {
    return String(option?.label ?? "") === String(rowValue ?? "");
  }
  return optionValueMatchesRow(option?.value, rowValue);
}

export function getDependentFieldKeysForControlColumn(
  conditionalFields: any[] | null | undefined,
  controlColumn: string
): string[] {
  if (!conditionalFields?.length || !controlColumn) return [];
  const keys: string[] = [];
  for (const cfg of conditionalFields) {
    if (!isFilterDependentDataAction(cfg)) continue;
    if (cfg?.Column !== controlColumn) continue;
    const deps = cfg?.DependentFilteredFields;
    if (!Array.isArray(deps)) continue;
    deps.forEach((d: any) => {
      if (d?.Field) keys.push(d.Field);
    });
  }
  return keys;
}
